import { keyframes } from '@emotion/react';
import { Box } from '@inflearn/ds-react-next';

const sparkleKeyframes = keyframes`
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(1) rotate(180deg);
    opacity: 1;
  }
  100% {
    transform: scale(0) rotate(360deg);
    opacity: 0;
  }
`;

const burstKeyframes = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.5);
    opacity: 0;
  }
`;

const starKeyframes = keyframes`
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const glowKeyframes = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.8);
  }
  50% {
    box-shadow: 0 0 30px 15px rgba(255, 215, 0, 0.4);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 215, 0, 0);
  }
`;

// 속도 배율 (낮을수록 빠름)
const SPEED = 0.5;

interface SelectionSparkleProps {
  isActive: boolean;
}

export const SelectionSparkle = ({ isActive }: SelectionSparkleProps) => {
  if (!isActive) {
    return null;
  }

  // 별 파티클 위치들
  const starPositions = [
    { x: -60, y: -40, delay: 0, size: 16 },
    { x: 60, y: -35, delay: 0.1, size: 14 },
    { x: -50, y: 40, delay: 0.15, size: 12 },
    { x: 55, y: 45, delay: 0.05, size: 15 },
    { x: 0, y: -55, delay: 0.2, size: 18 },
    { x: -70, y: 0, delay: 0.1, size: 13 },
    { x: 70, y: 5, delay: 0.15, size: 14 },
    { x: 0, y: 55, delay: 0.25, size: 16 },
    { x: -35, y: -50, delay: 0.08, size: 11 },
    { x: 40, y: -48, delay: 0.12, size: 12 },
    { x: -45, y: 48, delay: 0.18, size: 10 },
    { x: 45, y: 50, delay: 0.22, size: 13 },
  ];

  return (
    <Box
      pos="absolute"
      top="50%"
      left="50%"
      style={{
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 100,
      }}
    >
      {/* 중앙 버스트 효과 */}
      <Box
        pos="absolute"
        top="50%"
        left="50%"
        css={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(255, 215, 0, 0.8) 0%, rgba(255, 165, 0, 0.4) 50%, transparent 70%)',
          animation: `${burstKeyframes} ${0.6 * SPEED}s ease-out forwards`,
        }}
      />

      {/* 두 번째 버스트 링 */}
      <Box
        pos="absolute"
        top="50%"
        left="50%"
        css={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          border: '3px solid rgba(255, 215, 0, 0.6)',
          animation: `${burstKeyframes} ${0.8 * SPEED}s ease-out ${0.1 * SPEED}s forwards`,
          opacity: 0,
        }}
      />

      {/* 글로우 효과 */}
      <Box
        pos="absolute"
        top="50%"
        left="50%"
        css={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          animation: `${glowKeyframes} ${0.8 * SPEED}s ease-out forwards`,
        }}
      />

      {/* 별 파티클들 */}
      {starPositions.map((star, index) => {
        const getStarColor = () => {
          const remainder = index % 3;
          if (remainder === 0) {
            return '#FFD700';
          }
          if (remainder === 1) {
            return '#FFA500';
          }
          return '#FFEC8B';
        };

        return (
          <Box
            key={index}
            pos="absolute"
            top="50%"
            left="50%"
            css={{
              width: `${star.size}px`,
              height: `${star.size}px`,
              transform: 'translate(-50%, -50%)',
              animation: `${starKeyframes} ${0.8 * SPEED}s ease-out ${star.delay * SPEED}s forwards`,
              animationFillMode: 'both',
              '&::before': {
                content: '"\\2726"', // ✦ 별 문자
                position: 'absolute',
                top: star.y,
                left: star.x,
                fontSize: `${star.size}px`,
                color: getStarColor(),
                textShadow: '0 0 8px rgba(255, 215, 0, 0.8)',
                animation: `${sparkleKeyframes} ${0.6 * SPEED}s ease-out ${star.delay * SPEED}s forwards`,
              },
            }}
          />
        );
      })}

      {/* 추가 스파클 이펙트 */}
      {[...Array(6)].map((_, i) => {
        const angle = i * 60 * (Math.PI / 180);
        const distance = 50;
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance;

        return (
          <Box
            key={`sparkle-${i}`}
            pos="absolute"
            top="50%"
            left="50%"
            css={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#FFD700',
              transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
              boxShadow: '0 0 10px 2px rgba(255, 215, 0, 0.8)',
              animation: `${sparkleKeyframes} ${0.5 * SPEED}s ease-out ${i * 0.05 * SPEED}s forwards`,
            }}
          />
        );
      })}
    </Box>
  );
};
